import { customRequestIdleCallback } from '@/utils/requestIdleCallback';
import * as Sentry from '@sentry/react';
import {
  getIsChiefPartner,
  getIsDefaultPartner,
  getIsHarmonyPartner,
  getIsTelinPartner,
  getPartnerCode,
  initTrackingObserver,
  postHogInit,
  posthog,
  removeAllFetchCache,
} from '@zenlayer/portal-utils';
import {
  I18nApiService,
  isChromeSwitchOpened,
  setEditInherited,
  setLangJSON,
} from '@zenlayer/zen-intl18';
import { useRequest } from 'ahooks';
import { mapObjIndexed } from 'ramda';
import { useState } from 'react';
import { posthogConfig } from './useAutoLogin';

// init evnet tracker observer
initTrackingObserver();

export function getPortalEnv() {
  return localStorage.getItem('PORTAL_ENV');
}

export const getRuntimeInfo = () => {
  return window._zen_env_ || {};
};

const transformStrings = (i18nStrings: Record<string, string>) => {
  let rules: { regex: RegExp; replace: string }[] | undefined;
  if (!getIsDefaultPartner()) {
    rules = [
      {
        regex: /zenlayer/gi,
        replace: '',
      },
      {
        regex: /zeno/gi,
        replace: '',
      },
    ];
  }

  const replaceContent = (value: string) => {
    rules?.forEach((rule) => {
      value = value?.replace(rule.regex, rule.replace);
    });
    return value;
  };

  if (!rules?.length) {
    return i18nStrings;
  }

  return mapObjIndexed(replaceContent, i18nStrings);
};

const setLangByEnv = async (isOnline: boolean) => {
  try {
    let projectId = __I18N_CONFIG__.id;
    const partnerCode = getPartnerCode();
    const partner_projectId = partnerCode
      ? __I18N_CONFIG__.options?.partner_config?.[partnerCode]?.id
      : undefined;
    if (partner_projectId) {
      projectId = partner_projectId;
    }

    const { getI18nStrings } = new I18nApiService({
      env: getRuntimeInfo().env,
      isProduction: __PRODUCTION__,
      projectId,
      gitBranch: __GIT_BRANCH__,
      _setEditingProjectId:
        getIsDefaultPartner() ||
        getIsTelinPartner() ||
        getIsChiefPartner() ||
        getIsHarmonyPartner(),
    });
    if (isChromeSwitchOpened()) {
      setEditInherited(getIsDefaultPartner());
    }
    const i18nStrings = await getI18nStrings();
    setLangJSON({ text: transformStrings(i18nStrings) });
  } catch (e) {
    removeAllFetchCache();
    setLangJSON({ text: {} });
    console.error(e);
  }
};

/**
 * posthog 在 requestIdleCallback 里延后初始化，首屏的 getUser 常比它先回来
 * （2026-09 实测约 9% 的新鲜加载）。那一刻 posthog.identify 打到的是 posthog-js
 * 未初始化的兜底实例，被静默丢弃；依赖 get_distinct_id() 的 HubSpot 归因同步也会
 * 静默退出；员工账号的 re-init 还可能被随后才跑的客户 key 初始化覆盖回去。
 * 所以把「tracker 初始化完成」暴露成 promise，登录链路等它就绪再 identify。
 * 非 ONLINE 环境不初始化 tracker，promise 直接 resolve，等它的地方不会挂死。
 */
let markEventTrackerReady: () => void = () => {};
const eventTrackerReady = new Promise<void>((resolve) => {
  markEventTrackerReady = () => resolve();
});
export const whenEventTrackerReady = () => eventTrackerReady;

const initEventTracker = () => {
  customRequestIdleCallback(
    async () => {
      try {
        await postHogInit(
          'phc_FxlFsxavQymtBkGXyGGU3VBf0Rs0yiK1jVdyr9edB2y',
          posthogConfig
        );
      } finally {
        markEventTrackerReady();
      }

      Sentry.init({
        dsn: 'https://f09112d06d2633f85165a8e10d616cbf@o4507537049452544.ingest.us.sentry.io/4507537050894336',
        integrations: [
          posthog.sentryIntegration({
            organization: 'zenlayer',
            projectId: 1,
            // severityAllowList: ['error'],
          }),
          Sentry.browserTracingIntegration(),
        ],
        // Performance Monitoring
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [/^https:\/\/[^/]+\/api/],
      });
    },
    // 后台标签页 / 首屏长时间不空闲时 idle 可能迟迟不来，而登录链路在等这里：
    // 超时兜底保证等待有界
    { timeout: 3_000 }
  );
};

export default function useRuntimeInfo() {
  const [ready, setReady] = useState(false);
  const data = getRuntimeInfo();

  useRequest(async () => {
    let env;
    if (__PRODUCTION__) {
      env = data.online ? 'ONLINE' : 'TEST';
    } else {
      env = 'DEVELOPMENT';
    }

    if (env === 'ONLINE') {
      // 在生产环境引入
      initEventTracker();
    } else {
      markEventTrackerReady();
    }
    localStorage.setItem('PORTAL_ENV', env);
    await setLangByEnv(env === 'ONLINE');
    setReady(true);
  });

  return {
    ready,
  };
}
